/* eslint-disable jsx-a11y/anchor-is-valid */
import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import useJobHelpers from './hooks/useJobHelpers';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/src/shadComponents/ui/dialog';
import { Input } from '@/src/shadComponents/ui/input';
import { Button } from '@/src/shadComponents/ui/button';

const statuses: any = {
    Ready: 'text-green-700 bg-green-50 ring-green-600/20',
    Okay: 'text-gray-600 bg-gray-50 ring-gray-500/10',
    'Needs Artwork': 'text-red-700 bg-red-50 ring-red-600/10',
    'Needs Configuration': 'text-red-700 bg-red-50 ring-red-600/10',
};

function classNames(...classes: any[]) {
    return classes.filter(Boolean).join(' ');
}

interface ActivationsListProps {
    jobId: number;
}

export default function ActivationsList({ jobId }: ActivationsListProps) {
    const navigate = useNavigate();
    const [activations, setActivations] = useState<any[]>([]);
    const {
        getJobActivations,
        getOrgActivations,
        attachActivation,
        detachActivation,
    } = useJobHelpers();

    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [candidates, setCandidates] = useState<any[]>([]);
    const [search, setSearch] = useState('');
    const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
    const [savingId, setSavingId] = useState<number | null>(null);

    const refresh = async () => {
        const jobActivations = await getJobActivations(jobId);
        setActivations(jobActivations);
    };

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openPicker = async () => {
        setIsPickerOpen(true);
        setSearch('');
        setIsLoadingCandidates(true);
        try {
            const all = await getOrgActivations();
            // Only activations not already attached to a job can be added.
            setCandidates(
                all.filter(
                    (activation: any) =>
                        activation.jobId === null ||
                        activation.jobId === undefined,
                ),
            );
        } catch (error) {
            console.error(error);
            toast.error('Failed to load activations');
        } finally {
            setIsLoadingCandidates(false);
        }
    };

    const handleAttach = async (activationId: number) => {
        setSavingId(activationId);
        try {
            await attachActivation(activationId, jobId);
            setCandidates((prev) =>
                prev.filter((activation) => activation.id !== activationId),
            );
            await refresh();
            toast.success('Activation added to job');
        } catch (error) {
            console.error(error);
            toast.error('Failed to add activation');
        } finally {
            setSavingId(null);
        }
    };

    const handleDetach = async (activationId: number) => {
        setSavingId(activationId);
        try {
            await detachActivation(activationId);
            await refresh();
            toast.success('Activation removed from job');
        } catch (error) {
            console.error(error);
            toast.error('Failed to remove activation');
        } finally {
            setSavingId(null);
        }
    };

    const filteredCandidates = candidates.filter((activation) => {
        const term = search.trim().toLowerCase();
        if (!term) {
            return true;
        }
        return (
            activation.name?.toLowerCase().includes(term) ||
            activation.location?.toLowerCase().includes(term)
        );
    });

    return (
        <div className="">
            <tr className="text-sm leading-6 text-gray-900">
                <th
                    scope="colgroup"
                    colSpan={3}
                    className="relative isolate py-2 font-semibold"
                >
                    <div className="px-4 sm:px-8 w-max relative ">
                        Activations
                    </div>
                    <div className="absolute inset-y-0 right-full -z-10 w-screen border-b border-gray-200 bg-gray-50" />
                    <div className="absolute inset-y-0 left-0 -z-10 w-screen border-b border-gray-200 bg-gray-50">
                        <div className="absolute right-0 my-[6px] flex items-center gap-x-2">
                            <button
                                type="button"
                                onClick={openPicker}
                                className="px-3 py-1 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-sm"
                            >
                                Add existing
                            </button>
                            <a
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate('activation/create');
                                }}
                                className="mr-2 px-3 py-1 text-sm font-semibold text-white shadow-sm hover:bg-indigo-100 rounded-sm"
                            >
                                <Plus className="h-5 w-5 text-black" />
                            </a>
                        </div>
                    </div>
                </th>
            </tr>
            <div>
                <div className="overflow-hidden border-t border-gray-100">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
                            <table className="w-full text-left">
                                <thead className="sr-only">
                                    <tr>
                                        <th>Activation Name</th>
                                        <th className="hidden sm:table-cell">
                                            Location
                                        </th>
                                        <th>More details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {activations.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={3}
                                                className="py-6 text-sm text-gray-500"
                                            >
                                                No activations yet. Add an
                                                existing one or create a new
                                                activation.
                                            </td>
                                        </tr>
                                    )}
                                    {activations.map((activation) => (
                                        <tr
                                            key={activation.id}
                                            className="even:bg-gray-50"
                                        >
                                            <td className="relative py-5 pr-6">
                                                <div className="flex gap-x-6">
                                                    <div className="flex-auto">
                                                        <div className="flex items-start gap-x-3">
                                                            <div className="text-sm font-medium leading-6 text-gray-900">
                                                                {
                                                                    activation.name
                                                                }
                                                            </div>
                                                            {activation.status ? (
                                                                <div
                                                                    className={classNames(
                                                                        statuses[
                                                                            activation
                                                                                .status
                                                                        ],
                                                                        'rounded-md py-1 px-2 text-xs font-medium ring-1 ring-inset',
                                                                    )}
                                                                >
                                                                    {
                                                                        activation.status
                                                                    }
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                        <div className="mt-1 text-xs leading-5 text-gray-500">
                                                            {activation.type}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="absolute bottom-0 right-full h-px w-screen bg-gray-100" />
                                                <div className="absolute bottom-0 left-0 h-px w-screen bg-gray-100" />
                                            </td>
                                            <td className="hidden py-5 pr-6 sm:table-cell">
                                                <div className="text-sm leading-6 text-gray-900">
                                                    {activation.location}
                                                </div>
                                                <div className="mt-1 text-xs leading-5 text-gray-500">
                                                    {activation.description}
                                                </div>
                                            </td>
                                            <td className="py-5 text-right">
                                                <div className="flex justify-end items-center gap-x-4">
                                                    <a
                                                        href={`/activations/${activation.id}`}
                                                        className="text-sm font-medium leading-6 text-black hover:text-indigo-500"
                                                    >
                                                        View
                                                        <span className="hidden sm:inline">
                                                            {' '}
                                                            Activation &rarr;
                                                        </span>
                                                    </a>
                                                    <button
                                                        type="button"
                                                        disabled={
                                                            savingId ===
                                                            activation.id
                                                        }
                                                        onClick={() =>
                                                            handleDetach(
                                                                activation.id,
                                                            )
                                                        }
                                                        className="text-sm font-medium leading-6 text-gray-400 hover:text-red-600 disabled:opacity-50"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                                {activation.staff ? (
                                                    <div className="mt-1 text-xs leading-5 text-gray-500">
                                                        Owner{' '}
                                                        <span className="text-gray-900">
                                                            {
                                                                activation.staff.filter(
                                                                    (
                                                                        staff: any,
                                                                    ) =>
                                                                        staff.role ===
                                                                        'Production Manager',
                                                                )[0].name
                                                            }
                                                        </span>
                                                    </div>
                                                ) : null}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <Dialog open={isPickerOpen} onOpenChange={setIsPickerOpen}>
                <DialogContent className="max-w-xl">
                    <DialogHeader>
                        <DialogTitle>Add existing activations</DialogTitle>
                        <DialogDescription>
                            Only activations that aren't already part of a job
                            are shown.
                        </DialogDescription>
                    </DialogHeader>

                    <Input
                        placeholder="Search by name or location"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />

                    <div className="max-h-80 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-md">
                        {isLoadingCandidates && (
                            <div className="p-4 text-sm text-gray-500">
                                Loading activations...
                            </div>
                        )}
                        {!isLoadingCandidates &&
                            filteredCandidates.length === 0 && (
                                <div className="p-4 text-sm text-gray-500">
                                    No unassigned activations found.
                                </div>
                            )}
                        {!isLoadingCandidates &&
                            filteredCandidates.map((activation) => (
                                <div
                                    key={activation.id}
                                    className="flex items-center justify-between px-4 py-3"
                                >
                                    <div>
                                        <div className="text-sm font-medium text-gray-900">
                                            {activation.name}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {activation.location}
                                        </div>
                                    </div>
                                    <Button
                                        type="button"
                                        size="sm"
                                        disabled={savingId === activation.id}
                                        onClick={() =>
                                            handleAttach(activation.id)
                                        }
                                    >
                                        {savingId === activation.id
                                            ? 'Adding...'
                                            : 'Add'}
                                    </Button>
                                </div>
                            ))}
                    </div>

                    <div className="flex justify-end">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setIsPickerOpen(false)}
                        >
                            Done
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
